import { Box } from '@material-ui/core'
import { useState } from 'react'
import { BooleanInput, maxLength, minLength, required, SelectInput, TextInput } from 'react-admin'
import { Form } from 'react-final-form'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Button } from 'shared/components/Button/Button'
import { Handler } from 'shared/helpers/typeHelper'
import styled from 'styled-components'

import {
  createEmploymentHistory,
  EmploymentHistory,
  EmploymentHistoryJobType,
  EmploymentHistoryReasonLeaving,
  employmentJobTypeOptions,
  equipmentDrivenOptions,
  leavingReasonOptions,
  trailerTypeOptions,
  updateEmploymentHistory,
} from '../../models/employment'

interface UserEmploymentFormProps {
  employmentJob?: EmploymentHistory
  onSuccess: Handler
  onClose: Handler
  userId: string
}

export function UserEmploymentForm({ employmentJob, onClose, onSuccess, userId }: UserEmploymentFormProps) {
  const [isLoading, setIsLoading] = useState(false)
  const isEditing = Boolean(employmentJob)

  async function handleConfirm(values: any) {
    setIsLoading(true)
    const payload = {
      job_type: values.job_type,
      details: {
        dot_number: values.dot_number,
        company_name: values.company_name,
        company_address: values.company_address,

        can_contact_employer: values.can_contact_employer,
        is_own_authority: values.is_own_authority,
        leaving_reason: values.leaving_reason,
        leaving_reason_detail: values.leaving_reason_detail,
        equipment_type: values.equipment_type,
        trailer_type: values.trailer_type,
        contact_name: values.contact_name,
        contact_email: values.contact_email,
        contact_phone: values.contact_phone,
        subject_to_fmcsr_regulations: values.subject_to_fmcsr_regulations,
        subject_to_drug_and_alcohol_testing: values.subject_to_drug_and_alcohol_testing,
        start_date: values.start_date,
        end_date: values.currently_working_here ? null : values.end_date,
      },
    }
    if (isEditing) {
      await updateEmploymentHistory(employmentJob!.id, userId, payload)
    } else {
      await createEmploymentHistory(userId, payload)
    }

    onSuccess()
  }

  const initialValues = {
    job_type: isEditing ? employmentJob?.job_type : EmploymentHistoryJobType.DRIVING,
    dot_number: employmentJob?.details.dot_number,
    company_name: employmentJob?.details.company_name,
    company_address: employmentJob?.details.company_address,

    can_contact_employer: employmentJob?.details.can_contact_employer || false,
    is_own_authority: employmentJob?.details.is_own_authority || false,
    leaving_reason: employmentJob?.details?.leaving_reason,
    leaving_reason_detail: employmentJob?.details.leaving_reason_detail,
    equipment_type: employmentJob?.details.equipment_type,
    trailer_type: employmentJob?.details.trailer_type,
    contact_name: employmentJob?.details.contact_name,
    contact_email: employmentJob?.details.contact_email,
    contact_phone: employmentJob?.details.contact_phone,
    subject_to_fmcsr_regulations: employmentJob?.details.subject_to_fmcsr_regulations || false,
    subject_to_drug_and_alcohol_testing: employmentJob?.details.subject_to_drug_and_alcohol_testing || false,
    start_date: employmentJob?.details.start_date_formatted,
    end_date: employmentJob?.details.end_date_formatted,
    currently_working_here: Boolean(employmentJob?.details.end_date) === false,
  }

  function renderForm(formProps: any) {
    const { values } = formProps
    const isDrivingJob = values.job_type === EmploymentHistoryJobType.DRIVING
    return (
      <>
        <SelectInput
          source="job_type"
          label="Job Type"
          validate={[required()]}
          fullWidth
          choices={employmentJobTypeOptions}
          disabled={!isEditing}
        />
        <TextInput source="company_name" label="Employer" validate={[required()]} fullWidth />
        {isDrivingJob && (
          <>
            <TextInput
              source="dot_number"
              label="Employer dot number"
              validate={[
                minLength(1, 'Please enter a valid DOT number'),
                maxLength(8, 'Please enter a valid DOT number'),
              ]}
              fullWidth
            />
            <TextInput source="company_address" label="City, State" validate={[required()]} fullWidth />
          </>
        )}

        <BooleanInput
          source="is_own_authority"
          label="Is under own authority"
          validate={[required()]}
          fullWidth
        />

        <BooleanInput
          source="currently_working_here"
          label="Currently working here"
          validate={[required()]}
          fullWidth
        />
        <TextInput
          source="start_date"
          label="Start date (MM/YYYY)"
          validate={[
            required(),
            minLength(7, 'Please enter month and year in format `MM/YYYY`'),
            maxLength(7, 'Please enter month and year in format `MM/YYYY`'),
          ]}
          fullWidth
        />
        {values.currently_working_here === false && (
          <TextInput
            source="end_date"
            label="End date (MM/YYYY)"
            validate={[
              required(),
              minLength(7, 'Please enter month and year in format `MM/YYYY`'),
              maxLength(7, 'Please enter month and year in format `MM/YYYY`'),
            ]}
            fullWidth
          />
        )}

        {isDrivingJob && (
          <>
            {values.currently_working_here === false && (
              <>
                <SelectInput
                  source="leaving_reason"
                  label="Reason for leaving"
                  validate={[]}
                  fullWidth
                  choices={leavingReasonOptions}
                />
                {values.leaving_reason === EmploymentHistoryReasonLeaving.OTHER && (
                  <TextInput
                    source="leaving_reason_detail"
                    label="Reason for leaving detail"
                    validate={[]}
                    fullWidth
                  />
                )}
              </>
            )}

            <SelectInput
              source="equipment_type"
              label="Equipment driven"
              validate={[]}
              fullWidth
              choices={equipmentDrivenOptions}
            />

            <SelectInput
              source="trailer_type"
              label="Trailer"
              validate={[]}
              fullWidth
              choices={trailerTypeOptions}
            />

            <BooleanInput
              source="can_contact_employer"
              label="Can contact employer"
              validate={[required()]}
              fullWidth
            />

            {values.can_contact_employer === true && (
              <>
                <TextInput source="contact_name" label="Contact name" validate={[]} fullWidth />
                <TextInput source="contact_email" label="Contact email" validate={[]} fullWidth />
                <TextInput source="contact_phone" label="Contact phone" validate={[]} fullWidth />
              </>
            )}

            <BooleanInput
              source="subject_to_fmcsr_regulations"
              label="Subject to FMCSR regulations"
              validate={[required()]}
              fullWidth
            />

            <BooleanInput
              source="subject_to_drug_and_alcohol_testing"
              label="Subject to drug and alcohol testing"
              validate={[required()]}
              fullWidth
            />
          </>
        )}

        <Box mt={4} />

        <FlexBox gap="20px">
          <Button type="submit" size="large" onClick={formProps.handleSubmit} isLoading={isLoading}>
            Save
          </Button>
          <Button variant="secondary" size="large" onClick={onClose}>
            Cancel
          </Button>
        </FlexBox>
      </>
    )
  }

  return (
    <Wrap>
      <Form initialValues={initialValues} onSubmit={handleConfirm} render={renderForm} />
    </Wrap>
  )
}

const Wrap = styled.div`
  max-width: 424px;
`
